/**
 * Google Click ID capture + retrieval helpers.
 *
 * Google Ads no longer always sends `gclid` on auto-tagged URLs — since
 * iOS 14.5 + Performance Max + Enhanced Conversions, a meaningful share
 * of clicks arrive with `gbraid` (iOS-privacy-restricted) or `wbraid`
 * (web-browse-only) instead. Capturing only `gclid` (as we did pre-
 * 2026-05-29) loses 40-70% of ad attribution silently — those signups
 * read `gclid IS NULL` so the Stripe-webhook skips the offline-conv
 * upload, Google Ads sees 0 paid conversions, Smart Bidding never
 * learns, CPC stays elevated.
 *
 * This module captures all three identifiers on first visit, persists
 * for 90 days (Google's max attribution window), and exposes a single
 * `readClickIdentifiers()` helper that returns whichever one the user
 * arrived with. Upload code (`googleAdsConversion.ts`) chooses the
 * matching field on the ClickConversion payload.
 *
 * Storage is split across localStorage + a fallback cookie so Safari
 * private-mode + Firefox-strict-tracking users still get attribution.
 */

const STORAGE_KEY = 'eb_click_ids_v1';
const COOKIE_NAME = 'eb_click_ids';
// Legacy keys we still read for back-compat with pre-fix sessions.
const LEGACY_STORAGE_KEY = 'eb_gclid_v1';
const LEGACY_COOKIE_NAME = 'eb_gclid';
const MAX_AGE_DAYS = 90;

export type ClickIdentifierKind = 'gclid' | 'gbraid' | 'wbraid';

export interface ClickIdentifiers {
  /** First-touch click ID — whichever of the three landed first. */
  kind: ClickIdentifierKind;
  /** Actual click-identifier value. */
  value: string;
  /** ISO timestamp of first capture (first-touch attribution). */
  captured_at: string;
}

/**
 * Back-compat alias — `StoredGclid` was the pre-fix struct shape with
 * `{gclid, captured_at}`. Old cookies still in the wild get migrated
 * via {@link readClickIdentifiers} so existing trial-users don't lose
 * attribution mid-trial.
 */
interface LegacyStoredGclid {
  gclid: string;
  captured_at: string;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Read `?gclid=`, `?gbraid=`, or `?wbraid=` from the current URL and
 * persist whichever is present. Idempotent: subsequent calls with the
 * same identifier don't overwrite `captured_at` (first-touch wins).
 *
 * Precedence when multiple are present (rare but happens on iOS
 * fallback flows): gclid > gbraid > wbraid. gclid is the most
 * granular and most widely-accepted signal; the others are
 * privacy-restricted aggregates we use only when gclid is absent.
 */
export function captureGclidFromUrl(): void {
  if (!isBrowser()) return;
  const params = new URLSearchParams(window.location.search);

  // Order = precedence. We pick the first one that's present + valid.
  const candidates: ClickIdentifierKind[] = ['gclid', 'gbraid', 'wbraid'];
  let captured: { kind: ClickIdentifierKind; value: string } | null = null;
  for (const kind of candidates) {
    const value = params.get(kind);
    if (value && value.length >= 8 && value.length <= 200) {
      captured = { kind, value };
      break;
    }
  }
  if (!captured) return;

  const existing = readClickIdentifiers();
  if (existing && existing.kind === captured.kind && existing.value === captured.value) {
    return; // already captured, preserve original captured_at
  }

  const entry: ClickIdentifiers = {
    kind: captured.kind,
    value: captured.value,
    captured_at: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    /* Safari private-mode etc. — fall back to cookie only. */
  }

  const expires = new Date(Date.now() + MAX_AGE_DAYS * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(entry))}; expires=${expires}; path=/; SameSite=Lax`;
}

/**
 * Read whichever click-identifier was first-touch-captured, regardless
 * of kind. Migrates legacy `eb_gclid` storage into the new struct so
 * users mid-trial don't lose attribution when this code ships.
 */
export function readClickIdentifiers(): ClickIdentifiers | null {
  if (!isBrowser()) return null;

  // 1. New unified storage.
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ClickIdentifiers;
      if (parsed?.kind && parsed?.value) return parsed;
    }
  } catch {
    /* ignore */
  }
  const newCookie = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]+)`));
  if (newCookie) {
    try {
      const parsed = JSON.parse(decodeURIComponent(newCookie[1])) as ClickIdentifiers;
      if (parsed?.kind && parsed?.value) return parsed;
    } catch {
      /* ignore */
    }
  }

  // 2. Legacy gclid-only storage — back-compat for sessions that
  // captured before this module was generalised.
  try {
    const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (raw) {
      const legacy = JSON.parse(raw) as LegacyStoredGclid;
      if (legacy?.gclid) {
        return { kind: 'gclid', value: legacy.gclid, captured_at: legacy.captured_at };
      }
    }
  } catch {
    /* ignore */
  }
  const legacyCookie = document.cookie.match(new RegExp(`(?:^|; )${LEGACY_COOKIE_NAME}=([^;]+)`));
  if (legacyCookie) {
    try {
      const legacy = JSON.parse(decodeURIComponent(legacyCookie[1])) as LegacyStoredGclid;
      if (legacy?.gclid) {
        return { kind: 'gclid', value: legacy.gclid, captured_at: legacy.captured_at };
      }
    } catch {
      /* ignore */
    }
  }

  return null;
}

/**
 * Back-compat shim. Old callers in the codebase ask for `readGclid()`
 * and expect a `{gclid, captured_at}` struct. Returns null when the
 * captured identifier is `gbraid` or `wbraid` (those aren't gclids).
 * New code should use {@link readClickIdentifiers} directly.
 *
 * @deprecated Use `readClickIdentifiers()` and check `kind`.
 */
export function readGclid(): { gclid: string; captured_at: string } | null {
  const ids = readClickIdentifiers();
  if (!ids || ids.kind !== 'gclid') return null;
  return { gclid: ids.value, captured_at: ids.captured_at };
}

export function clearGclid(): void {
  if (!isBrowser()) return;
  for (const key of [STORAGE_KEY, LEGACY_STORAGE_KEY]) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
  for (const name of [COOKIE_NAME, LEGACY_COOKIE_NAME]) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
  }
}
